/**
 * Admin-Panel P1 (docs/KONZEPT-ADMIN-PANEL.md § "P1 — Lesen"): Mandant-Detail, rein lesend.
 * Backend: `GET /api/admin/tenants/:id` — Stammdaten inkl. `stripeAccountId` + volle
 * Mitgliederliste (`stackUserId`, `role`, `email`, `displayName`). Falsche/unbekannte `id` oder
 * kein Zugriff enden serverseitig auf demselben 404 (kein Existenz-Orakel).
 *
 * Kein eigener Router-Pfad (siehe Kopfkommentar `tenants-liste.tsx`): `tenantId` kommt als Prop
 * aus dem `selectedTenantId`-State der Admin-Shell, "Zurueck" ruft `onBack` statt zu navigieren.
 *
 * P2 (docs/LOOP-ADMIN-PANEL.md § P2 "Frontend Bearbeiten-Formular + Mitgliederverwaltung"):
 * Schreibpfade `PATCH /api/admin/tenants/:id`, `POST/PATCH/DELETE
 * /api/admin/tenants/:id/members(/:memberId)` (`apps/api/src/routes/admin.ts`). Muster fuer
 * `apiMutate` + Fehleranzeige uebernommen aus `pages/settings.tsx` (`TeamTab`): eigener
 * Busy-/Error-State je Aktion, `err.message` direkt anzeigen (der Server liefert bereits einen
 * fertigen deutschen Text, z.B. "Dieser Slug ist bereits von einem anderen Mandanten vergeben."
 * bei 409 `slug_taken` bzw. "Der letzte Inhaber ... kann nicht ... werden." bei 409
 * `last_owner`) - kein stiller Fehlschlag, keine falsche Erfolgsmeldung. Nach jeder erfolgreichen
 * Aktion `reload()` (dasselbe `useApi`-Reload wie ueberall im Repo), damit die Ansicht den
 * aktuellen Stand aus der Datenbank zeigt statt eines optimistisch angenommenen.
 *
 * P3 (docs/LOOP-ADMIN-PANEL.md § P3 "Impersonation"): „Support-Zugriff starten"-Formular ruft
 * `POST /api/admin/tenants/:id/impersonate` (Pflichtfeld `reason`, optionale Dauer in Minuten,
 * serverseitige Obergrenze `IMPERSONATION_DAUER_MAX_MINUTEN = 120`, siehe `routes/admin.ts`).
 * Bei Erfolg springt der Browser SOFORT auf die normale Mandanten-Subdomain dieses Mandanten -
 * der Admin agiert danach als Owner INNERHALB der normalen App (Konzept § Impersonation, Punkt 6:
 * das eigentliche Bedienen passiert dort, nicht auf dieser Admin-Seite). `urlFuerSubdomain`
 * (lib/subdomain.ts) uebernimmt dieselbe Technik wie `switchTenant` in `ui/AppShell.tsx` (erstes
 * Hostname-Label ersetzen) statt eine eigene `APP_BASE_DOMAIN`-Konstante im Frontend zu brauchen.
 *
 * P4 (docs/LOOP-ADMIN-PANEL.md § P4 "Frontend Loeschen-Dialog"): „Mandant loeschen" oeffnet die
 * generische `ConfirmSlugModal` (Admin-Bereich V2, `ui/components.tsx`) mit Pflicht-Texteingabe
 * des EXAKTEN Betriebsnamens (P7, Antoine F21; vorher Slug) - kein Ein-Klick-Loeschen, der Bestaetigen-Button bleibt in der Komponente
 * selbst deaktiviert, bis die Eingabe `=== erwarteterText` ist. `onConfirm` ruft `DELETE
 * /api/admin/tenants/:id` mit `{ confirmName }` = der eingetippte Text (`routes/admin.ts` prueft
 * ihn serverseitig gegen `tenants.name`). **Wichtigster Punkt:** eine
 * 409-Antwort - egal ob `name_mismatch` oder ein `ok:false`-Ergebnis wie laufende Sitzungen
 * (`error: ergebnis.grund`, z.B. `sitzung_stumm`) - wirft in `apiMutate`, `ConfirmSlugModal` faengt
 * das im eigenen `catch` ab und zeigt die Fehlermeldung inline im weiterhin offenen Dialog; es
 * gibt keinen Code-Pfad, der zwischen den beiden 409-Faellen unterscheidet oder bei einem von
 * beiden faelschlich Erfolg anzeigt. Nur der Code NACH dem erfolgreich awaiteten
 * `apiMutate`-Aufruf in `confirmDelete` laeuft bei Erfolg: zeigt die Erfolgsmeldung
 * (`window.alert`, Muster `event-detail.tsx`) und navigiert per `onBack()` zurueck zur Liste.
 *
 * P1 Admin-Bereich V2 (docs/KONZEPT-ADMIN-BEREICH.md § "Mandanten-Detail, 8 Tabs",
 * docs/LOOP-ADMIN-BEREICH.md P1 fuenftes Kriterium): `TabBar` (P0, `ui/components.tsx`) traegt
 * seitdem "Übersicht" (dieser gesamte, unveraendert gebliebene Stammdaten-/Mitglieder-/
 * Impersonations-Block bleibt dort), "Integrationen", "Gesundheit", "Audit", "Nutzung". Jeder neue
 * Tab ist eine eigene Unterkomponente unter `./tenant-detail/`, ausser dem Audit-Tab
 * (`AuditLogTabelle`, aus `./AuditLogSeite.tsx` herausgeloest — Wiederverwendung statt Duplikat,
 * wie im Konzept ausdruecklich gewuenscht). Alle Tabs sind reine `{tab === "x" && <...>}`-Bloecke
 * (Muster `settings.tsx`) — sie mounten erst bei Auswahl und feuern ihre eigene `useApi`, kein
 * Vorabladen fuer inaktive Tabs.
 *
 * P4 (docs/LOOP-ADMIN-BEREICH.md § P4, fuenftes/letztes substanzielles Kriterium vor dem
 * Mini-Gate): neuer Tab "Einstellungen" (`./tenant-detail/EinstellungenTab.tsx`) — Betriebsschalter
 * (Modul-/Benachrichtigungs-Schalter, `agentMode`, `mailFreigabeModus`, Firmenkopf,
 * Business-Profil) ueber `GET/PATCH /api/admin/tenants/:id/settings` (Migration 378) editierbar.
 * `acceptanceFlow`/`paymentPathway` bleiben dort aussen vor (eigenes Mini-Gate, siehe
 * Kopfkommentar `EinstellungenTab.tsx`).
 */
import { type FormEvent, useState } from "react";
import { useApi, apiMutate } from "../../hooks/useApi";
import { formatDate, ROLE_LABEL, tenantStatusMeta } from "../../ui/format";
import { Button, ConfirmSlugModal, EmptyState, ErrorNote, Icon, LoadingRows, Modal, StatusPill, TabBar, useBestaetigung, Select, Input } from "../../ui/components";
import { einladungsMeldung, type OwnerEinladung } from "./mandant-neu";
import { urlFuerSubdomain } from "../../lib/subdomain";
import { ladeDateiHerunter } from "../../lib/files";
import { AuditLogTabelle } from "./AuditLogSeite";
import { IntegrationenTab } from "./tenant-detail/IntegrationenTab";
import { GesundheitTab } from "./tenant-detail/GesundheitTab";
import { NutzungTab } from "./tenant-detail/NutzungTab";
import { EinstellungenTab } from "./tenant-detail/EinstellungenTab";

const IMPERSONATION_DAUER_DEFAULT_MINUTEN = 30;
const IMPERSONATION_DAUER_MAX_MINUTEN = 120;

export interface AdminTenantMember {
  stackUserId: string;
  role: string;
  email: string | null;
  displayName: string | null;
}

export interface AdminTenantDetail {
  id: string;
  slug: string;
  name: string;
  stripeAccountId: string | null;
  createdAt: string;
  members: AdminTenantMember[];
  /** Optional statt Pflichtfeld — s. Kopfkommentar `AdminTenantRow` in `tenants-liste.tsx`. */
  status?: string;
}

const ROLE_OPTIONS: Array<"owner" | "staff"> = ["owner", "staff"];

type DetailTab = "uebersicht" | "integrationen" | "gesundheit" | "audit" | "nutzung" | "einstellungen";

export function TenantDetail({ tenantId, onBack }: { tenantId: string; onBack: () => void }) {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const { data, loading, error, reload } = useApi<AdminTenantDetail>(`/api/admin/tenants/${tenantId}`);

  // Tab-Auswahl (P1 Admin-Bereich V2, siehe Kopfkommentar) - "Übersicht" bleibt der Startzustand,
  // damit bestehende Tests (z.B. `admin-tenant-loeschen.test.tsx`), die direkt nach dem Laden auf
  // "Mandant löschen" klicken, unveraendert funktionieren.
  const [tab, setTab] = useState<DetailTab>("uebersicht");

  // Bearbeiten-Formular (Name/Slug/Stripe-Konto) - eigenes Modal statt Inline, Muster
  // `settings.tsx` TeamTab (Owner-Transfer-Modal).
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editStripe, setEditStripe] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  function openEdit() {
    if (!data) return;
    setEditName(data.name);
    setEditSlug(data.slug);
    setEditStripe(data.stripeAccountId ?? "");
    setEditError(null);
    setEditOpen(true);
  }

  async function saveEdit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEditBusy(true);
    setEditError(null);
    try {
      await apiMutate(`/api/admin/tenants/${tenantId}`, "PATCH", {
        name: editName.trim(),
        slug: editSlug.trim(),
        stripeAccountId: editStripe.trim() === "" ? null : editStripe.trim(),
      });
      setEditOpen(false);
      reload();
    } catch (err) {
      // Server liefert bei 409 slug_taken bereits einen fertigen deutschen Text - direkt zeigen,
      // kein stiller Fehlschlag (das Modal bleibt offen, kein Erfolgs-Signal).
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditBusy(false);
    }
  }

  // Mitglied hinzufuegen.
  const [newMemberId, setNewMemberId] = useState("");
  const [newMemberRole, setNewMemberRole] = useState<"owner" | "staff">("staff");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  async function addMember(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setAddBusy(true);
    setAddError(null);
    try {
      await apiMutate(`/api/admin/tenants/${tenantId}/members`, "POST", {
        stackUserId: newMemberId.trim(),
        role: newMemberRole,
      });
      setNewMemberId("");
      setNewMemberRole("staff");
      reload();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : String(err));
    } finally {
      setAddBusy(false);
    }
  }

  // Mitglied per E-Mail einladen (dieselbe Route wie der Onboarding-Assistent, mandant-neu.tsx).
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"owner" | "staff">("staff");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<OwnerEinladung | null>(null);

  async function inviteMember(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInviteBusy(true);
    setInviteError(null);
    setInviteResult(null);
    try {
      const einladung = await apiMutate<OwnerEinladung>(`/api/admin/tenants/${tenantId}/invites`, "POST", {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteResult(einladung);
      setInviteEmail("");
      setInviteRole("staff");
    } catch (err) {
      // 409 invite_pending/already_member: der Server liefert den fertigen deutschen Text.
      setInviteError(err instanceof Error ? err.message : String(err));
    } finally {
      setInviteBusy(false);
    }
  }

  // Rolle aendern / entfernen je Mitglied - ein gemeinsamer rowBusy/rowError reicht (Muster
  // `settings.tsx` TeamTab: nur eine Zeile gleichzeitig aktiv, `select`/Button sind waehrenddessen
  // disabled).
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  async function changeRole(stackUserId: string, newRole: string) {
    setRowBusy(stackUserId);
    setRowError(null);
    try {
      await apiMutate(`/api/admin/tenants/${tenantId}/members/${stackUserId}`, "PATCH", { role: newRole });
      reload();
    } catch (err) {
      // 409 last_owner liefert bereits "Der letzte Inhaber ... kann nicht degradiert werden."
      setRowError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }

  async function removeMember(stackUserId: string) {
    if (!(await bestaetigen({ titel: "Mitglied wirklich aus diesem Mandanten entfernen?", bestaetigen: "Entfernen", gefahr: true }))) return;
    setRowBusy(stackUserId);
    setRowError(null);
    try {
      await apiMutate(`/api/admin/tenants/${tenantId}/members/${stackUserId}`, "DELETE");
      reload();
    } catch (err) {
      // 409 last_owner liefert bereits "Der letzte Inhaber ... kann nicht entfernt werden."
      setRowError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }

  // Mandant loeschen (P4) - `ConfirmSlugModal` (Admin-Bereich V2, `ui/components.tsx`) kapselt
  // Texteingabe/Busy/Fehleranzeige selbst; hier bleibt nur der offen/geschlossen-Zustand.
  const [deleteOpen, setDeleteOpen] = useState(false);

  async function confirmDelete(eingabe: string) {
    if (!data) return;
    // Die ECHTE Eingabe geht raus (ungetrimmt), nicht `data.name` - der Server vergleicht sie
    // selbst exakt mit `tenants.name` (P7). Die UI-Sperre in `ConfirmSlugModal` ist nur Komfort.
    await apiMutate<{ ok: true; slug: string; sitzungen: string[]; datenbank: unknown }>(
      `/api/admin/tenants/${tenantId}`,
      "DELETE",
      { confirmName: eingabe },
    );
    // Nur dieser Zweig laeuft bei einer 200-Antwort - eine 409 (name_mismatch ODER ein
    // ok:false-Ergebnis wie laufende Sitzungen) wirft in apiMutate; `ConfirmSlugModal` faengt das
    // im eigenen catch ab, zeigt die Fehlermeldung inline und haelt den Dialog offen (kein
    // falsches Erfolgs-Signal, identisch zum bisherigen Verhalten).
    setDeleteOpen(false);
    await bestaetigen({ titel: `Mandant „${data.slug}“ wurde gelöscht.`, bestaetigen: "OK", abbrechen: false });
    onBack();
  }

  // DSGVO-Export (P7, F27): ZIP-Download (export.json + Anhänge), Dateiname aus Content-Disposition.
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  async function exportieren() {
    if (!data) return;
    setExportBusy(true);
    setExportError(null);
    try {
      await ladeDateiHerunter(`/api/admin/tenants/${tenantId}/export`, `maestro-export-${data.slug}.zip`);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export fehlgeschlagen.");
    } finally {
      setExportBusy(false);
    }
  }

  // Support-Zugriff starten (Impersonation, P3).
  const [impReason, setImpReason] = useState("");
  const [impDauer, setImpDauer] = useState(String(IMPERSONATION_DAUER_DEFAULT_MINUTEN));
  const [impBusy, setImpBusy] = useState(false);
  const [impError, setImpError] = useState<string | null>(null);

  async function starteImpersonation(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    setImpBusy(true);
    setImpError(null);
    try {
      const dauerMinuten = impDauer.trim() === "" ? undefined : Number(impDauer);
      await apiMutate<{ grantId: string; tenantId: string; expiresAt: string }>(
        `/api/admin/tenants/${tenantId}/impersonate`,
        "POST",
        { reason: impReason.trim(), dauerMinuten },
      );
      // Der eigentliche Zweck der Impersonation passiert INNERHALB der normalen Mandanten-App
      // (Konzept § Impersonation, Punkt 6) - sofortige Weiterleitung auf die normale Subdomain
      // dieses Mandanten, wo das globale Banner (ImpersonationBanner) den aktiven Grant zeigt.
      window.location.assign(urlFuerSubdomain(data.slug));
    } catch (err) {
      setImpError(err instanceof Error ? err.message : String(err));
      setImpBusy(false);
    }
  }

  return (
    <div className="mt-6">
      {bestaetigungsDialog}
      <button type="button" className="btn-secondary" onClick={onBack}>
        <Icon name="arrow_back" className="text-[16px]" /> Zurück zur Mandantenliste
      </button>

      {loading && (
        <div className="card mt-4 overflow-hidden">
          <LoadingRows rows={4} />
        </div>
      )}
      {error && (
        <div className="card mt-4 overflow-hidden">
          <ErrorNote message={error} onRetry={reload} />
        </div>
      )}
      {!loading && !error && !data && (
        <div className="card mt-4 overflow-hidden">
          <EmptyState icon="apartment" title="Mandant nicht gefunden" />
        </div>
      )}

      {data && (
        <div className="mt-4">
          <TabBar
            tabs={[
              { value: "uebersicht", label: "Übersicht" },
              { value: "integrationen", label: "Integrationen" },
              { value: "gesundheit", label: "Gesundheit" },
              { value: "audit", label: "Audit" },
              { value: "nutzung", label: "Nutzung" },
              { value: "einstellungen", label: "Einstellungen" },
            ]}
            value={tab}
            onValueChange={(v) => setTab(v as DetailTab)}
          />
        </div>
      )}

      {data && tab === "integrationen" && <IntegrationenTab stripeAccountId={data.stripeAccountId} />}
      {data && tab === "gesundheit" && <GesundheitTab tenantId={tenantId} />}
      {data && tab === "audit" && (
        <div className="mt-4">
          <AuditLogTabelle tenantId={tenantId} />
        </div>
      )}
      {data && tab === "nutzung" && <NutzungTab tenantId={tenantId} />}
      {data && tab === "einstellungen" && <EinstellungenTab tenantId={tenantId} />}

      {data && tab === "uebersicht" && (
        <div className="card mt-4 overflow-hidden">
          <div className="flex items-start justify-between gap-3 border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{data.name}</h2>
                {/* Lifecycle-Status (P2, Migration 375) — dieselbe Herleitung wie die Spalte in
                    `tenants-liste.tsx`, siehe `tenantStatusMeta` (`ui/format.ts`). */}
                <StatusPill meta={tenantStatusMeta(data.status)} testId="tenant-detail-status" />
              </div>
              <p className="font-mono text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{data.slug}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button type="button" className="btn-secondary" onClick={openEdit}>
                <Icon name="edit" className="text-[16px]" /> Bearbeiten
              </button>
              <Button type="button" variant="secondary" onClick={exportieren} disabled={exportBusy}>
                <Icon name="download" className="text-[16px]" /> {exportBusy ? "Export läuft …" : "Daten exportieren"}
              </Button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 rounded-[9px] bg-error px-3.5 py-2 font-label-md text-label-md text-on-error transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setDeleteOpen(true)}
              >
                <Icon name="delete" className="text-[16px]" /> Mandant löschen
              </button>
            </div>
          </div>
          <dl className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-2">
            <div>
              <dt className="font-label-md text-[11px] font-semibold uppercase tracking-wide text-secondary-fixed-dim">
                Stripe-Konto
              </dt>
              <dd className="mt-0.5 font-mono text-body-sm text-on-background dark:text-inverse-on-surface">
                {data.stripeAccountId ?? "—"}
              </dd>
            </div>
            <div>
              <dt className="font-label-md text-[11px] font-semibold uppercase tracking-wide text-secondary-fixed-dim">
                Erstellt am
              </dt>
              <dd className="mt-0.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                {formatDate(data.createdAt)}
              </dd>
            </div>
          </dl>
          {exportError && <div className="px-4 pb-3"><ErrorNote message={exportError} /></div>}

          <div className="border-t border-outline-variant/30 px-4 py-3 dark:border-outline/30">
            <h3 className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Mitglieder{data.members.length > 0 ? ` (${data.members.length})` : ""}
            </h3>
            {rowError && <ErrorNote message={rowError} />}
            {data.members.length === 0 ? (
              <p className="mt-2 font-body-sm text-[12.5px] text-secondary-fixed-dim">Keine Mitglieder.</p>
            ) : (
              <ul className="mt-2 divide-y divide-outline-variant/30 dark:divide-outline/30">
                {data.members.map((m) => (
                  <li key={m.stackUserId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                    <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                      {m.displayName ?? m.email ?? m.stackUserId}
                    </span>
                    {/* Nur zusaetzlich zeigen, wenn die E-Mail nicht bereits als Hauptzeile steht
                        (kein displayName -> die Hauptzeile faellt selbst schon auf die E-Mail
                        zurueck, siehe oben - sonst stuende dieselbe Adresse doppelt da). */}
                    {m.displayName && m.email && (
                      <span className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">{m.email}</span>
                    )}
                    <div className="ml-auto">
                      <Select
                        value={m.role}
                        disabled={rowBusy === m.stackUserId}
                        onValueChange={(v) => void changeRole(m.stackUserId, v)}
                        className="!w-auto !py-1"
                        ariaLabel={`Rolle von ${m.displayName ?? m.email ?? m.stackUserId}`}
                        options={ROLE_OPTIONS.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }))}
                      />
                    </div>
                    <button
                      type="button"
                      className="font-label-md text-label-md text-error disabled:opacity-50"
                      disabled={rowBusy === m.stackUserId}
                      onClick={() => void removeMember(m.stackUserId)}
                    >
                      Entfernen
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <form className="mt-4 flex flex-wrap items-end gap-3" onSubmit={(e) => void addMember(e)}>
              <label className="min-w-[220px] flex-1">
                <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  Stack-User-ID
                </span>
                <input
                  type="text"
                  required
                  value={newMemberId}
                  onChange={(e) => setNewMemberId(e.target.value)}
                  placeholder="z.B. 3f2a…"
                  className="input"
                />
              </label>
              <label>
                <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  Rolle
                </span>
                <Select
                  value={newMemberRole}
                  onValueChange={(v) => setNewMemberRole(v as typeof newMemberRole)}
                  options={ROLE_OPTIONS.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }))}
                />
              </label>
              <button type="submit" className="btn-primary !py-2" disabled={addBusy || !newMemberId.trim()}>
                {addBusy ? "Fügt hinzu …" : "Mitglied hinzufügen"}
              </button>
            </form>
            {addError && <ErrorNote message={addError} />}

            <form className="mt-6 flex flex-wrap items-end gap-3" onSubmit={(e) => void inviteMember(e)}>
              <label className="min-w-[220px] flex-1">
                <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  Mitglied einladen (E-Mail)
                </span>
                <Input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="name@betrieb.de"
                />
              </label>
              <label>
                <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  Rolle
                </span>
                <Select
                  value={inviteRole}
                  onValueChange={(v) => setInviteRole(v as typeof inviteRole)}
                  options={ROLE_OPTIONS.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }))}
                  ariaLabel="Rolle der Einladung"
                />
              </label>
              <Button type="submit" className="!py-2" disabled={inviteBusy || !inviteEmail.trim()}>
                {inviteBusy ? "Lädt ein …" : "Einladung senden"}
              </Button>
            </form>
            {inviteError && <ErrorNote message={inviteError} />}
            {inviteResult && (
              <p className="mt-2 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface" role="status">
                {einladungsMeldung(inviteResult)}
              </p>
            )}
          </div>
        </div>
      )}

      {data && tab === "uebersicht" && (
        <div className="card mt-4 overflow-hidden">
          <div className="border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
            <h3 className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Support-Zugriff starten (Impersonation)
            </h3>
            <p className="mt-1 font-body-sm text-[12.5px] text-secondary-fixed-dim">
              Legt eine echte, befristete Mitgliedschaft an und leitet dich sofort in die normale
              Oberflaeche dieses Mandanten weiter. Dort erscheint ein durchgaengiges Banner mit
              „Beenden"-Knopf, das den Zugriff jederzeit wieder aufhebt.
            </p>
          </div>
          <form className="flex flex-col gap-3 p-4" onSubmit={(e) => void starteImpersonation(e)}>
            <label className="block">
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Grund (Pflicht)
              </span>
              <textarea
                required
                rows={2}
                value={impReason}
                onChange={(e) => setImpReason(e.target.value)}
                placeholder="z.B. Support-Fall #1234 - Kunde meldet fehlerhafte Rechnung"
                className="input"
              />
            </label>
            <label className="max-w-[220px]">
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Dauer (Minuten, max. {IMPERSONATION_DAUER_MAX_MINUTEN})
              </span>
              <input
                type="number"
                min={1}
                max={IMPERSONATION_DAUER_MAX_MINUTEN}
                value={impDauer}
                onChange={(e) => setImpDauer(e.target.value)}
                className="input"
              />
            </label>
            {impError && <ErrorNote message={impError} />}
            <div>
              <button type="submit" className="btn-primary" disabled={impBusy || !impReason.trim()}>
                {impBusy ? "Startet …" : "Support-Zugriff starten"}
              </button>
            </div>
          </form>
        </div>
      )}

      <Modal
        open={editOpen}
        onClose={() => (!editBusy ? setEditOpen(false) : undefined)}
        width="480px"
        title="Mandant bearbeiten"
        onSubmit={(e) => void saveEdit(e)}
        footer={
          <>
            <Button type="button" variant="secondary" disabled={editBusy} onClick={() => setEditOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={editBusy || !editName.trim() || !editSlug.trim()}>
              {editBusy ? "Speichert …" : "Speichern"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <label className="block">
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Name
            </span>
            <input type="text" required className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Slug
            </span>
            <input type="text" required className="input font-mono" value={editSlug} onChange={(e) => setEditSlug(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Stripe-Konto
            </span>
            <input type="text" className="input font-mono" value={editStripe} onChange={(e) => setEditStripe(e.target.value)} placeholder="acct_…" />
          </label>
          {editError && <ErrorNote message={editError} />}
        </div>
      </Modal>

      <ConfirmSlugModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        titel="Mandant löschen"
        warnungstext={
          <>
            Diese Aktion ist <strong>unwiderruflich</strong>. Alle Daten von „{data?.name}"
            {" "}(<span className="font-mono">{data?.slug}</span>) werden endgültig gelöscht. Zur
            Bestätigung den Betriebsnamen exakt eintippen.
          </>
        }
        erwarteterText={data?.name ?? ""}
        bestaetigungslabel="Endgültig löschen"
        gefahrTon
      />
    </div>
  );
}
